import orthancAPI from '@ohif/core/src/utils/orthancAPI';

import {
  REPORT_ATTACHMENT_ID,
  buildReportEnvelope,
} from '../../AZmed/Data/reportSeries';
import {
  getOfflineReportGeneration,
  hasLocalDuplication,
  readOfflineReport,
} from '../../AZmed/Data/offlineReportStore';

/**
 * Persistent cache of the radiology reports, with revalidation.
 *
 * Two regimes, told apart by whether this profile has a local duplication, see
 * isDiskOnlyReportSource.
 *
 * Without one, four tiers read in this order: a module Map for the current page, the
 * offline directory on disk, the Cache API for the pages before this one, and the
 * network. An entry is served immediately and checked against the server afterwards,
 * so a report amended after it was cached still reaches the radiologist — an
 * attachment is mutable, unlike the DICOM instances the other caches hold.
 *
 * With one, the disk is the only source: the Cache API and the network are both cut,
 * and the memory tier only answers with what it read from disk. A disk miss is then
 * the final answer, "this study has no report", not a reason to look further.
 *
 * Every tier is keyed by series, and there is deliberately no study-keyed way in: a
 * report belongs to the series it hangs from, so answering a study with the series a
 * previous visit happened to resolve would hand back a report the user's current
 * selection does not contain.
 *
 * A copy read from the offline directory is never revalidated, see `offlineOrigin`.
 * Because nothing revalidates it, nothing would ever replace it either, so a re-run
 * duplication invalidates it through the index generation instead, see
 * `offlineGeneration`.
 */

const CACHE_NAME = 'orthanc-reports';
const KEY_PREFIX = '/__azviewer/report-cache/';

/** Envelopes of this page, so a study round trip paints without any await. */
const reportMemory = new Map();

/** Coalesces concurrent readers of the same series. Never a durable cache. */
const inFlight = new Map();

/**
 * The Cache API is opened once per page: `caches.open` hits disk, and doing it
 * per read cost a measurable round trip in UseGetImage before it was memoised.
 * @type {Promise<Cache|null>|null}
 */
let reportCachePromise = null;

/** Opens the report cache, once, and never rejects. */
const openReportCache = () => {
  if (reportCachePromise) return reportCachePromise;

  if (!('caches' in window)) {
    reportCachePromise = Promise.resolve(null);

    return reportCachePromise;
  }

  reportCachePromise = caches.open(CACHE_NAME).catch(() => null);

  return reportCachePromise;
};

/**
 * @param {string} seriesInstanceUID
 * @returns {string} The synthetic key of that series. Never fetched, and outside
 *   every proxied prefix, so nothing can intercept it.
 */
const cacheKey = seriesInstanceUID =>
  `${KEY_PREFIX}${encodeURIComponent(seriesInstanceUID)}`;

/** Writes the envelope to the Cache API. Best effort, never awaited by a caller. */
const persist = async envelope => {
  const cache = await openReportCache();
  if (!cache) return;

  await cache.put(
    cacheKey(envelope.seriesInstanceUID),
    new Response(JSON.stringify(envelope), {
      headers: { 'Content-Type': 'application/json' },
    })
  );
};

/**
 * Stores an envelope. The memory tier is what the render reads, so it is written
 * synchronously and the disk write is not awaited: waiting on caches.open plus a
 * put would delay the paint by tens of milliseconds for nothing.
 *
 * The one choke point every network-derived envelope goes through. Passing null as
 * the generation is what drops the offline marks: from here on the copy we hold
 * came from the server, so it is revalidatable again.
 * @param {Object} envelope
 */
const putEnvelope = envelope => {
  writeMemory(envelope, null);
  persist(envelope).catch(() => undefined);
};

/** Reads an envelope from the Cache API, null on absence or on any failure. */
const readEnvelope = async seriesInstanceUID => {
  const cache = await openReportCache();
  if (!cache) return null;

  try {
    const response = await cache.match(cacheKey(seriesInstanceUID));
    if (!response) return null;

    // Normalised, so a stored `null` body answers the documented null rather than
    // travelling on as a falsy envelope.
    return (await response.json()) || null;
  } catch (error) {
    return null;
  }
};

/**
 * The validator to store for a 200, preferring the ETag and falling back to the
 * md5 route: cross-origin the header is filtered out, a response body is not.
 * @param {Response} response A 200 from the attachment route
 * @param {string} seriesOrthancId
 * @returns {Promise<Object|null>} `{ kind, value }`, null when neither is available
 */
const readValidator = async (response, seriesOrthancId) => {
  const etag = response.headers.get('ETag');

  if (etag) return { kind: 'etag', value: etag };

  const md5 = await orthancAPI
    .fetchSeriesAttachmentMd5(seriesOrthancId, REPORT_ATTACHMENT_ID)
    .catch(() => null);

  return md5 ? { kind: 'md5', value: md5 } : null;
};

/**
 * Series whose copy came from the offline directory. Those are served as they were
 * written and never checked: the whole point of the caching button is a study that
 * opens with no server behind it, and a conditional GET on every open spends a
 * request that on the offline path can only ever time out.
 *
 * The accepted cost is that a report amended on the server after the duplication
 * ran is never picked up for that study — re-running the button is what refreshes
 * it.
 *
 * Kept in the module rather than in the envelope: that shape is shared with the
 * writer and persisted on disk, where "this copy came from disk" would be a field
 * every reader of every stored copy reads as true.
 */
const offlineOrigin = new Set();

/**
 * SeriesInstanceUID → the index generation the disk was consulted under for it.
 *
 * One meaning only: "the index generation the disk copy we hold was read under".
 * Written and cleared by writeMemory in lockstep with `offlineOrigin`, so the two
 * always carry the same keys. The generation is bumped by every duplication run, so
 * a value that no longer matches the current one says a new report.json may be
 * waiting — the only signal available, since a disk copy is never revalidated and
 * would otherwise stay pinned until the page is reloaded.
 *
 * Never persisted, and that is what keeps it sound: reportMemory, offlineOrigin and
 * this counter are all page-scoped and restart from nothing together, whether or not
 * the directory access survived the reload — it does whenever the browser still holds
 * the grant, since the study loader restores the handle before the panel renders. A
 * counter outliving reportMemory could suppress a re-read that is legitimately due.
 */
const offlineGeneration = new Map();

/**
 * @param {string} [seriesInstanceUID]
 * @returns {boolean} Whether the disk was consulted for that series under an index
 *   generation a duplication has since replaced. The `has` test carries as much
 *   weight as the comparison: a series never looked up on disk is not outdated, and
 *   without it a first read would behave as if it were
 */
const isDiskReadOutdated = seriesInstanceUID =>
  offlineGeneration.has(seriesInstanceUID) &&
  offlineGeneration.get(seriesInstanceUID) !== getOfflineReportGeneration();

/**
 * The one and only writer of the memory tier.
 *
 * Structural rather than documented, because the invariant is easy to break: every
 * write has to settle the two module marks that qualify the entry, and the Cache
 * API tier used to set the Map on its own — which, now that entries are removed,
 * would leave an ex-offline series exempt from revalidation for the whole page.
 * @param {Object} envelope
 * @param {number|null} diskGeneration The index generation the envelope was read
 *   from the offline directory under; null for a server or Cache API copy. Anything
 *   that is not a number is treated as the latter, so a call site that forgets the
 *   argument costs one needless request rather than pinning an outdated report.
 */
const writeMemory = (envelope, diskGeneration) => {
  const { seriesInstanceUID } = envelope;

  reportMemory.set(seriesInstanceUID, envelope);

  if (typeof diskGeneration !== 'number') {
    offlineOrigin.delete(seriesInstanceUID);
    offlineGeneration.delete(seriesInstanceUID);

    return;
  }

  offlineOrigin.add(seriesInstanceUID);
  offlineGeneration.set(seriesInstanceUID, diskGeneration);
};

/**
 * @param {string} seriesInstanceUID
 * @returns {boolean} Whether the copy we hold came from disk and a duplication has
 *   reset the index since. Scoped to `offlineOrigin` on purpose: a server-derived
 *   copy is revalidated on every read and refreshes itself, so only a disk copy can
 *   outlive the file it was read from
 */
const isOutdatedOfflineCopy = seriesInstanceUID =>
  offlineOrigin.has(seriesInstanceUID) && isDiskReadOutdated(seriesInstanceUID);

/**
 * The one and only reader of the memory tier, mirroring writeMemory, and the only
 * place an entry is dropped.
 *
 * Synchronous, and traversed by the peek, because useStudyReport reads it during
 * render and any non-null envelope makes isLoading false: an outdated entry
 * this test missed would be painted as the final answer, with no spinner and no
 * indicator that it is a superseded medical report. Deleting is idempotent and the
 * answer stable, so calling it from a render stays safe under a double invocation.
 * @param {string} [seriesInstanceUID]
 * @returns {Object|undefined} The held envelope, undefined when there is none or
 *   when a fresh duplication has superseded it
 */
const readMemory = seriesInstanceUID => {
  const envelope = seriesInstanceUID
    ? reportMemory.get(seriesInstanceUID)
    : undefined;

  if (!envelope) return undefined;

  if (!isOutdatedOfflineCopy(seriesInstanceUID)) return envelope;

  // Removed rather than flagged, so no reader has to know about the flag. The
  // offline marks are left as they stand: readCachedReport goes to disk first in
  // any case, and whichever tier refills the entry settles them through writeMemory.
  reportMemory.delete(seriesInstanceUID);

  return undefined;
};

/**
 * Takes an offline envelope into the memory tier and records where it came from.
 * @param {Object} envelope An envelope just read from the offline directory
 * @param {number} generation The index generation it was read under
 * @returns {Object} The same envelope
 */
const adoptOfflineEnvelope = (envelope, generation) => {
  writeMemory(envelope, generation);

  // A copy we deliberately never check must not wear the flag that means "the
  // check failed": clearing it here covers the series whose earlier network check
  // failed in this page before the disk answered.

  return envelope;
};

/**
 * Reads the offline directory for a series and adopts what it holds.
 * @param {string} seriesInstanceUID
 * @returns {Promise<Object|null>} The envelope, null when the disk has none
 */
const readDisk = async seriesInstanceUID => {
  // Captured before the await, never after: a duplication finishing while this
  // read is in flight would otherwise stamp the new generation onto the older file
  // and pin it for good. Stamping the older one costs one extra disk read on the
  // next visit, which is the conservative way to be wrong.
  const generation = getOfflineReportGeneration();
  const envelope = await readOfflineReport(seriesInstanceUID);

  return envelope ? adoptOfflineEnvelope(envelope, generation) : null;
};

/**
 * @param {string} seriesInstanceUID
 * @returns {boolean} Whether the copy we hold for that series came from disk
 */
const isOfflineCopy = seriesInstanceUID => offlineOrigin.has(seriesInstanceUID);

/**
 * Whether the local copy is the only source of reports this profile accepts.
 *
 * Exported because the rule spans the cache and its caller: this module stops
 * answering from the Cache API, and useStudyReport stops asking the server. Read
 * fresh on every call rather than captured once, so the first study opened after a
 * duplication is already governed by it.
 * @returns {boolean}
 */
export const isDiskOnlyReportSource = () => hasLocalDuplication();

/**
 * The held envelope, once it has been checked against the disk-only rule.
 *
 * The single gate both memory readers go through, because the tier can hold a copy
 * that came from the network or the Cache API earlier in this same page — a study
 * opened online before the duplication was launched. Serving it under the rule would
 * be the very fallback the rule forbids, only harder to see, so it is refused and the
 * disk gets asked instead.
 *
 * Refused rather than deleted: the entry is the reader's own record of the tier, and
 * `readMemory` owns removals. A disk hit overwrites it through writeMemory anyway; a
 * disk miss leaves it in place and costs one directory lookup per read, which is the
 * price of never showing an unauthorised report.
 * @param {string} [seriesInstanceUID]
 * @returns {Object|undefined}
 */
const servableFromMemory = seriesInstanceUID => {
  const envelope = readMemory(seriesInstanceUID);

  if (!envelope) return undefined;

  if (isDiskOnlyReportSource() && !isOfflineCopy(seriesInstanceUID)) {
    return undefined;
  }

  return envelope;
};

/** Resolves the Orthanc series id, from the envelope when we already have one. */
const resolveSeriesId = async (seriesInstanceUID, known) =>
  known || (await orthancAPI.lookupOrthancID(seriesInstanceUID));

/**
 * Cold path: no envelope anywhere. Two requests, as before the cache existed.
 * @param {string} seriesInstanceUID
 * @returns {Promise<Object>} The stored envelope
 */
const fetchFresh = async seriesInstanceUID => {
  const seriesOrthancId = await resolveSeriesId(seriesInstanceUID);

  // The series exists in the study, so a missing Orthanc id means the server
  // refused or is unreachable rather than "there is no report". Do not cache it:
  // a failure is not an outcome.
  if (!seriesOrthancId) {
    throw new Error(`Could not resolve series ${seriesInstanceUID}`);
  }

  const response = await orthancAPI.fetchSeriesAttachmentResponse(
    seriesOrthancId,
    REPORT_ATTACHMENT_ID
  );

  if (response.status === 404) {
    // Cached too: most studies have no report, and re-asking on every study
    // change would spend two requests to learn nothing.
    const envelope = buildReportEnvelope({
      seriesInstanceUID,
      seriesOrthancId,
      report: null,
      validator: null,
    });
    putEnvelope(envelope);

    return envelope;
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch series attachment: ${response.status}`);
  }

  const report = await response.json();
  const envelope = buildReportEnvelope({
    seriesInstanceUID,
    seriesOrthancId,
    report,
    validator: await readValidator(response, seriesOrthancId),
  });
  putEnvelope(envelope);

  return envelope;
};

/**
 * Checks a stored envelope against the server and answers the envelope to use: the
 * same one when it is still current, when the server could not be reached or when
 * the check failed, a fresh one when the report changed, an empty one when it was
 * withdrawn.
 *
 * This function has no way to report an error state, on purpose: we are holding
 * valid content, and a failed check must never replace it with an error.
 * @param {Object} envelope
 * @param {boolean} [retried] Set on the one retry allowed after a stale Orthanc
 *   id, so the bound is structural rather than resting on the lookup being stable
 * @returns {Promise<Object>}
 */
export const revalidate = async (envelope, retried = false) => {
  const { seriesInstanceUID, validator } = envelope;

  // A disk copy is served as written and never checked, see `offlineOrigin`, and
  // before every request below: on a duplicated profile they can only time out.
  if (isOfflineCopy(seriesInstanceUID)) return envelope;

  try {
    const seriesOrthancId = await resolveSeriesId(
      seriesInstanceUID,
      envelope.seriesOrthancId
    );

    if (!seriesOrthancId) return envelope;

    // The md5 route when the ETag is unreadable: its body always comes through,
    // where a header does not cross origins unless exposed.
    if (validator && validator.kind === 'md5') {
      const md5 = await orthancAPI.fetchSeriesAttachmentMd5(
        seriesOrthancId,
        REPORT_ATTACHMENT_ID
      );

      if (md5 && md5 === validator.value) return envelope;
    }

    const response = await orthancAPI.fetchSeriesAttachmentResponse(
      seriesOrthancId,
      REPORT_ATTACHMENT_ID,
      {
        ifNoneMatch:
          validator && validator.kind === 'etag' ? validator.value : undefined,
      }
    );

    if (response.status === 304) return envelope;

    if (response.status === 404) {
      // A stored id can be dead: an Orthanc id is derived from the content, so
      // deleting and re-importing a series changes it, and the old one answers
      // 404 on everything. Concluding "withdrawn" there would turn an existing
      // report into a cached absence. Only trust the 404 on a freshly resolved id.
      if (envelope.report && envelope.seriesOrthancId && !retried) {
        const resolved = await orthancAPI.lookupOrthancID(seriesInstanceUID);

        // lookupOrthancID answers null for "not found" and for a network failure
        // alike, so a transient error must not be read as a withdrawn report.
        if (!resolved) return envelope;

        if (resolved !== envelope.seriesOrthancId) {
          return revalidate({ ...envelope, seriesOrthancId: resolved }, true);
        }
      }

      // A report withdrawn on the server has to disappear here too.
      const absent = buildReportEnvelope({
        seriesInstanceUID,
        seriesOrthancId,
        report: null,
        validator: null,
      });
        putEnvelope(absent);

      return absent;
    }

    if (!response.ok) return envelope;

    const nextValidator = await readValidator(response, seriesOrthancId);

    const updated = buildReportEnvelope({
      seriesInstanceUID,
      seriesOrthancId,
      report: await response.json(),
      validator: nextValidator,
    });
    putEnvelope(updated);

    return updated;
  } catch (error) {
    console.warn('[reportCache] Revalidation failed:', error);

    return envelope;
  }
};

/**
 * The envelope of this page, without touching disk. Meant to be called during
 * render: an effect runs after paint, which would flash a placeholder on every
 * study round trip.
 * @param {string} seriesInstanceUID
 * @returns {Object|undefined}
 */
export const peekReport = seriesInstanceUID =>
  servableFromMemory(seriesInstanceUID);

/**
 * The stored envelope of a series, from whichever tier is allowed to answer.
 *
 * Takes no AbortSignal on purpose: the work is shared between callers, so tying
 * it to one caller's lifetime would let a unmounting reader abort the request a
 * remounting one is waiting on.
 * @param {string} seriesInstanceUID
 * @returns {Promise<Object|null>} The envelope, null when no allowed tier holds one.
 *   Under the disk-only rule that null is a conclusion — see isDiskOnlyReportSource,
 *   and useStudyReport, which stops there instead of fetching
 */
export const readCachedReport = async seriesInstanceUID => {
  const local = servableFromMemory(seriesInstanceUID);
  if (local) return local;

  // The disk comes first, unconditionally: a copy written by the caching button is
  // authoritative and exempt from revalidation, so reaching the Cache API before it
  // is what spent a request on the very studies the user cached to have none. A
  // Cache API hit clears the offline marks through writeMemory, which also disarms
  // the guard in `revalidate` — the disk copy has to be found before that happens.
  //
  // The price is a directory lookup plus a read of a few hundred bytes per series,
  // the inventory index behind it being already memoised for the page. Free without
  // a directory: readOfflineReport answers null the moment there is no live handle.
  const offline = await readDisk(seriesInstanceUID);
  if (offline) return offline;

  // Once a duplication exists the disk has spoken, and a miss is an absence. Cutting
  // the chain here rather than in the caller as well is what keeps the promise the
  // caching button makes: a duplicated viewer opens a study without a single request,
  // whether or not that study turned out to have a report. Reached before `caches`
  // is even opened, so nothing outside the directory is so much as consulted.
  if (isDiskOnlyReportSource()) return null;

  const stored = await readEnvelope(seriesInstanceUID);
  if (stored) {
    // Through writeMemory, so a series that was offline and comes back this way
    // loses its exemption from revalidation instead of keeping it for good.
    writeMemory(stored, null);

    return stored;
  }

  // No local tier holds one. Null is not "there is no report" either: the caller
  // goes on to ask the server.
  return null;
};

/**
 * Fetches a report the cache does not hold, coalescing concurrent callers.
 * @param {string} seriesInstanceUID
 * @returns {Promise<Object>} The stored envelope
 */
export const fetchReport = seriesInstanceUID => {
  const pending = inFlight.get(seriesInstanceUID);
  if (pending) return pending;

  // Deleted whatever the outcome, unlike retrieveStudyMetadata where the promise
  // *is* the cache: keeping a rejected one would pin an error forever.
  const promise = fetchFresh(seriesInstanceUID).finally(() => {
    inFlight.delete(seriesInstanceUID);
  });

  inFlight.set(seriesInstanceUID, promise);

  return promise;
};
